// Links in the FAQ answers. The page renders each answer paragraph as HTML
// from this function; the FAQPage JSON-LD keeps the plain text.

// Programme / sister-site links. Brand names are kept in English across all
// locales, so the same phrase list linkifies every language. First occurrence
// per answer only, longest phrase first so "SOLS Energy" wins over "Energy".
const BRAND_LINKS: { phrase: string; href: string; external?: boolean }[] = [
  { phrase: "SOLS Solar Academy", href: "/solar-academy" },
  { phrase: "SOLS 24/7 Group", href: "https://sols247.com", external: true },
  // "SOLS Energy" is deliberately not linked. The Ad Grants Website policy
  // restricts links that push visitors to a commercial site, and solsenergy.com
  // is a for-profit subsidiary with no on-domain equivalent. The brand name
  // still reads as plain text in the answers.
  { phrase: "Energy Access", href: "/energy-access" },
  { phrase: "Skills Hub", href: "/skills-hub" },
];

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const anchor = (href: string, text: string, external?: boolean) =>
  external
    ? `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`
    : `<a href="${href}">${text}</a>`;

// An email address, the Skills Hub address, or the bare domain (not followed by
// a path), matched in one pass so a domain inside an email address stays part
// of the email's link. An email ends on a word character, so a full stop after
// it is left out of the link.
const ADDRESS =
  /([\w.+-]+@[\w-]+\.[\w.-]+\w)|(sols\.foundation\/skills-hub)|\bsols\.foundation\b(?!\/)/g;

/**
 * Turns a plain-text paragraph into HTML with email, programme and
 * sister-site links. `linked` tracks brand phrases already linked earlier in
 * the same answer.
 */
export function linkifyParagraph(text: string, linked: Set<string>): string {
  let html = escapeHtml(text);
  for (const { phrase, href, external } of BRAND_LINKS) {
    if (linked.has(phrase)) continue;
    const idx = html.indexOf(phrase);
    if (idx === -1) continue;
    html =
      html.slice(0, idx) +
      anchor(href, phrase, external) +
      html.slice(idx + phrase.length);
    linked.add(phrase);
  }
  return html.replace(ADDRESS, (m, email, skillsHub) =>
    email ? anchor(`mailto:${m}`, m) : anchor(skillsHub ? "/skills-hub" : "/", m),
  );
}
